/**
 * MicaraHR realtime event contracts.
 *
 * These names and payloads are the wire format the realtime transport carries.
 * The backend's WebSocket gateway emits a subset of them; the mock in-memory
 * bus emits others that predate it. This file is the union, and the source of
 * truth both sides must import rather than hard-coding strings.
 */
import type {
  ApprovalRequestType,
  ApprovalStatus,
  AttendanceRiskFlagCode,
  AttendanceSecurityDecision,
  AttendanceStatus,
  ISODateString,
  ISODateTimeString,
  NotificationType,
  OfflineSyncOutcome,
} from '@micarahr/shared-types';

export const EVENT_NAMES = {
  // Authentication
  AUTH_SESSION_CREATED: 'auth.session.created',
  AUTH_SESSION_EXPIRED: 'auth.session.expired',
  AUTH_DEVICE_REGISTERED: 'auth.device.registered',
  AUTH_DEVICE_REVOKED: 'auth.device.revoked',

  // Employee
  EMPLOYEE_CREATED: 'employee.created',
  EMPLOYEE_PROFILE_UPDATED: 'employee.profile.updated',
  EMPLOYEE_DOCUMENT_ADDED: 'employee.document.added',
  EMPLOYEE_DOCUMENT_EXPIRING: 'employee.document.expiring',
  EMPLOYEE_MANAGER_CHANGED: 'employee.manager.changed',

  // Attendance
  ATTENDANCE_TIME_IN_CREATED: 'attendance.time_in.created',
  ATTENDANCE_BREAK_STARTED: 'attendance.break.started',
  ATTENDANCE_BREAK_ENDED: 'attendance.break.ended',
  ATTENDANCE_TIME_OUT_CREATED: 'attendance.time_out.created',
  ATTENDANCE_RECORD_FLAGGED: 'attendance.record.flagged',
  ATTENDANCE_RECORD_APPROVED: 'attendance.record.approved',
  ATTENDANCE_RECORD_CORRECTED: 'attendance.record.corrected',
  ATTENDANCE_CORRECTION_SUBMITTED: 'attendance.correction.submitted',
  ATTENDANCE_CORRECTION_APPROVED: 'attendance.correction.approved',
  ATTENDANCE_CORRECTION_REJECTED: 'attendance.correction.rejected',
  ATTENDANCE_OFFLINE_SYNCHRONIZED: 'attendance.offline.synchronized',

  // Scheduling
  SCHEDULE_ASSIGNED: 'schedule.assigned',
  SCHEDULE_UPDATED: 'schedule.updated',
  SCHEDULE_CHANGE_REQUESTED: 'schedule.change_requested',
  SCHEDULE_CHANGE_APPROVED: 'schedule.change_approved',
  SCHEDULE_CHANGE_REJECTED: 'schedule.change_rejected',

  // Leave
  LEAVE_REQUEST_SUBMITTED: 'leave.request.submitted',
  LEAVE_REQUEST_APPROVED: 'leave.request.approved',
  LEAVE_REQUEST_REJECTED: 'leave.request.rejected',
  LEAVE_REQUEST_CANCELLED: 'leave.request.cancelled',
  LEAVE_BALANCE_UPDATED: 'leave.balance.updated',

  // Overtime
  OVERTIME_REQUEST_SUBMITTED: 'overtime.request.submitted',
  OVERTIME_REQUEST_APPROVED: 'overtime.request.approved',
  OVERTIME_REQUEST_REJECTED: 'overtime.request.rejected',
  OVERTIME_REQUEST_CANCELLED: 'overtime.request.cancelled',

  // Payroll
  PAYROLL_PERIOD_OPENED: 'payroll.period.opened',
  PAYROLL_PERIOD_PROCESSED: 'payroll.period.processed',
  // Names the backend actually emits — the three above predate it and are
  // kept for the mock bus until its emitters are migrated.
  PAYROLL_PERIOD_CREATED: 'payroll.period.created',
  PAYROLL_PERIOD_STATUS_CHANGED: 'payroll.period.status_changed',
  PAYROLL_CALCULATION_COMPLETED: 'payroll.calculation.completed',
  PAYSLIP_PUBLISHED: 'payslip.published',
  PAYSLIP_ACKNOWLEDGED: 'payslip.acknowledged',

  // Timesheets
  TIMESHEET_PERIOD_LOCKED: 'timesheet.period.locked',

  // Communication
  ANNOUNCEMENT_PUBLISHED: 'announcement.published',
  POLICY_PUBLISHED: 'policy.published',
  POLICY_ACKNOWLEDGEMENT_REQUIRED: 'policy.acknowledgement.required',
  NOTIFICATION_CREATED: 'notification.created',

  // Manager
  APPROVAL_ASSIGNED: 'approval.assigned',
  APPROVAL_COMPLETED: 'approval.completed',

  // Connectivity (client-local, never sent over the wire)
  CONNECTIVITY_CHANGED: 'connectivity.changed',
} as const;

export type EventName = (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];

interface BaseEventPayload {
  /** Employee the event concerns. */
  employeeId: string;
  occurredAt: ISODateTimeString;
}

export interface AuthSessionCreatedPayload extends BaseEventPayload {
  userId: string;
  deviceId: string;
  roles: string[];
}

export interface AuthSessionExpiredPayload extends BaseEventPayload {
  userId: string;
  reason: 'timeout' | 'revoked' | 'signed_out' | 'simulated';
}

export interface AuthDevicePayload extends BaseEventPayload {
  deviceId: string;
  deviceName: string;
  status: 'registered' | 'pending_approval' | 'revoked';
}

export interface EmployeeProfileUpdatedPayload extends BaseEventPayload {
  fields: string[];
}

export interface EmployeeDocumentPayload extends BaseEventPayload {
  documentId: string;
  documentName: string;
  expiresOn: ISODateString | null;
}

export interface EmployeeManagerChangedPayload extends BaseEventPayload {
  previousManagerId: string | null;
  managerId: string;
  managerName: string;
}

export interface AttendanceEventPayload extends BaseEventPayload {
  recordId: string;
  eventId: string;
  date: ISODateString;
  status: AttendanceStatus;
  decision: AttendanceSecurityDecision;
  riskFlags: AttendanceRiskFlagCode[];
  serverTimestamp: ISODateTimeString | null;
}

export interface AttendanceRecordFlaggedPayload extends BaseEventPayload {
  recordId: string;
  date: ISODateString;
  riskFlags: AttendanceRiskFlagCode[];
  reason: string;
}

export interface AttendanceRecordReviewedPayload extends BaseEventPayload {
  recordId: string;
  date: ISODateString;
  reviewerName: string;
  comment: string | null;
}

export interface AttendanceCorrectionPayload extends BaseEventPayload {
  correctionId: string;
  referenceCode: string;
  date: ISODateString;
  status: ApprovalStatus;
  decidedBy: string | null;
  comment: string | null;
}

export interface AttendanceOfflineSyncPayload extends BaseEventPayload {
  queueItemId: string;
  outcome: OfflineSyncOutcome;
  recordId: string | null;
  delayMinutes: number | null;
}

export interface SchedulePayload extends BaseEventPayload {
  date: ISODateString;
  shiftName: string | null;
  assignmentId: string | null;
  summary: string;
}

export interface ScheduleChangePayload extends BaseEventPayload {
  requestId: string;
  referenceCode: string;
  targetDate: ISODateString;
  status: ApprovalStatus;
  comment: string | null;
}

export interface LeaveRequestPayload extends BaseEventPayload {
  requestId: string;
  referenceCode: string;
  leaveTypeName: string;
  startDate: ISODateString;
  endDate: ISODateString;
  totalDays: number;
  status: ApprovalStatus;
  decidedBy: string | null;
  comment: string | null;
}

export interface LeaveBalanceUpdatedPayload extends BaseEventPayload {
  leaveTypeId: string;
  leaveTypeName: string;
  availableDays: number;
  changeDays: number;
  reason: string;
}

export interface OvertimeRequestPayload extends BaseEventPayload {
  requestId: string;
  referenceCode: string;
  date: ISODateString;
  estimatedHours: number;
  approvedHours: number | null;
  status: ApprovalStatus;
  decidedBy: string | null;
  comment: string | null;
}

export interface PayrollPeriodPayload extends BaseEventPayload {
  periodId: string;
  periodLabel: string;
  payDate: ISODateString;
  status: string;
}

export interface PayslipPayload extends BaseEventPayload {
  payslipId: string;
  periodLabel: string;
  payDate: ISODateString;
  netPayMinorUnits: number;
}

/*
 * Backend-emitted events. These payloads mirror what the API's outbox actually
 * publishes, and deliberately do not extend BaseEventPayload — the backend's
 * organisational events are not scoped to a single employee.
 */

export interface EmployeeCreatedPayload {
  employeeId: string;
  employeeNumber: string;
  /** Reference code of the candidate the employee was converted from. */
  fromCandidate: string;
}

export interface PayrollPeriodCreatedPayload {
  periodId: string;
  referenceCode: string;
  label: string;
}

export interface PayrollPeriodStatusChangedPayload {
  periodId: string;
  from: string;
  to: string;
}

export interface PayrollCalculationCompletedPayload {
  periodId: string;
  runSequence: number;
  employeeCount: number;
  exceptionCount: number;
}

export interface TimesheetPeriodLockedPayload {
  periodId: string;
  label: string;
}

export interface AnnouncementPayload extends BaseEventPayload {
  announcementId: string;
  title: string;
  category: string;
  priority: 'normal' | 'important' | 'urgent';
  requiresAcknowledgement: boolean;
}

export interface PolicyPayload extends BaseEventPayload {
  documentId: string;
  title: string;
  version: string;
  deadline: ISODateString | null;
}

export interface NotificationCreatedPayload extends BaseEventPayload {
  notificationId: string;
  type: NotificationType;
  title: string;
  body: string;
  deepLink: string | null;
  dedupeKey: string;
}

export interface ApprovalAssignedPayload extends BaseEventPayload {
  approvalId: string;
  approverId: string;
  type: ApprovalRequestType;
  referenceCode: string;
  requesterName: string;
  summary: string;
}

export interface ApprovalCompletedPayload extends BaseEventPayload {
  approvalId: string;
  approverId: string;
  approverName: string;
  type: ApprovalRequestType;
  referenceCode: string;
  status: ApprovalStatus;
  comment: string | null;
}

export interface ConnectivityChangedPayload {
  employeeId: string;
  occurredAt: ISODateTimeString;
  state: 'online' | 'offline';
  pendingOfflineEvents: number;
}

/** Map of event name → payload. The bus is typed against this. */
export interface EventPayloadMap {
  'auth.session.created': AuthSessionCreatedPayload;
  'auth.session.expired': AuthSessionExpiredPayload;
  'auth.device.registered': AuthDevicePayload;
  'auth.device.revoked': AuthDevicePayload;

  'employee.created': EmployeeCreatedPayload;
  'employee.profile.updated': EmployeeProfileUpdatedPayload;
  'employee.document.added': EmployeeDocumentPayload;
  'employee.document.expiring': EmployeeDocumentPayload;
  'employee.manager.changed': EmployeeManagerChangedPayload;

  'attendance.time_in.created': AttendanceEventPayload;
  'attendance.break.started': AttendanceEventPayload;
  'attendance.break.ended': AttendanceEventPayload;
  'attendance.time_out.created': AttendanceEventPayload;
  'attendance.record.flagged': AttendanceRecordFlaggedPayload;
  'attendance.record.approved': AttendanceRecordReviewedPayload;
  'attendance.record.corrected': AttendanceRecordReviewedPayload;
  'attendance.correction.submitted': AttendanceCorrectionPayload;
  'attendance.correction.approved': AttendanceCorrectionPayload;
  'attendance.correction.rejected': AttendanceCorrectionPayload;
  'attendance.offline.synchronized': AttendanceOfflineSyncPayload;

  'schedule.assigned': SchedulePayload;
  'schedule.updated': SchedulePayload;
  'schedule.change_requested': ScheduleChangePayload;
  'schedule.change_approved': ScheduleChangePayload;
  'schedule.change_rejected': ScheduleChangePayload;

  'leave.request.submitted': LeaveRequestPayload;
  'leave.request.approved': LeaveRequestPayload;
  'leave.request.rejected': LeaveRequestPayload;
  'leave.request.cancelled': LeaveRequestPayload;
  'leave.balance.updated': LeaveBalanceUpdatedPayload;

  'overtime.request.submitted': OvertimeRequestPayload;
  'overtime.request.approved': OvertimeRequestPayload;
  'overtime.request.rejected': OvertimeRequestPayload;
  'overtime.request.cancelled': OvertimeRequestPayload;

  'payroll.period.opened': PayrollPeriodPayload;
  'payroll.period.processed': PayrollPeriodPayload;
  'payroll.period.created': PayrollPeriodCreatedPayload;
  'payroll.period.status_changed': PayrollPeriodStatusChangedPayload;
  'payroll.calculation.completed': PayrollCalculationCompletedPayload;
  'payslip.published': PayslipPayload;
  'payslip.acknowledged': PayslipPayload;

  'timesheet.period.locked': TimesheetPeriodLockedPayload;

  'announcement.published': AnnouncementPayload;
  'policy.published': PolicyPayload;
  'policy.acknowledgement.required': PolicyPayload;
  'notification.created': NotificationCreatedPayload;

  'approval.assigned': ApprovalAssignedPayload;
  'approval.completed': ApprovalCompletedPayload;

  'connectivity.changed': ConnectivityChangedPayload;
}

/** An event as it travels on the bus. */
export interface MicaraHrEvent<TName extends EventName = EventName> {
  id: string;
  name: TName;
  payload: EventPayloadMap[TName];
  emittedAt: ISODateTimeString;
  /** Which surface produced the event — useful in the Simulation Centre. */
  origin: 'mock-service' | 'simulation' | 'client';
}

/** Union of every possible event, for exhaustive switch handling. */
export type AnyMicaraHrEvent = {
  [K in EventName]: MicaraHrEvent<K>;
}[EventName];
